import { useIsMutating } from "@tanstack/react-query";

import { ProductPage } from "@/clients/contentful";
import { useUI } from "@/common/contexts/ui.context";
import { useAddToCartMutation } from "@/features/basket/hooks/use-add-to-cart";
import { useStoreProduct } from "@/features/product/contexts/product-context";

type AddToCartButtonProps = {
  product: ProductPage;
  children: (arg: AddToCartButtonChildrenProps) => React.ReactNode;
};

export type AddToCartButtonChildrenProps = {
  isLoading: boolean;
  handleAddToBasket: () => void;
};

export function AddToCartButton({ product, children }: AddToCartButtonProps) {
  const { mutateAsync: addToCart } = useAddToCartMutation();
  const { setModalConfig, closeModal, openCart } = useUI();
  const { config, warnings, deliveryOption } = useStoreProduct();
  const isAddToCartMutating = useIsMutating({ mutationKey: ["ADD_TO_CART"] });
  const isLoading = !!isAddToCartMutating;

  async function addToBasket() {
    if (!product.fields.sku || !config) return;

    const sku = product.fields.sku;
    const versions = +(config.versions ?? 0);
    let quantity = typeof config?.quantity === "number" ? config.quantity : 1;
    if (versions) {
      quantity = quantity * versions;
    }

    await addToCart({ sku, config, quantity, deliveryOption });

    closeModal();
    openCart();
  }

  function addToBasketModalCheck() {
    if (warnings.length) {
      return setModalConfig({
        view: "PRODUCT_WARNING",
        data: { product, warnings, addToBasket },
        autoOpen: true,
      });
    } else if (product.fields.addToBasketModal) {
      return setModalConfig({
        view: "PRODUCT_VIEW",
        data: { product, warnings, addToBasket },
        autoOpen: true,
      });
    }
    return addToBasket();
  }

  return children({ isLoading, handleAddToBasket: addToBasketModalCheck });
}
